import { usePayment } from "@/components/fields/payment/use-payment"
import { api } from "@/lib/api"
import { useFormStore } from "@/stores/use-form-store"
import { logger } from "@/utils/logger"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { AxiosError } from "axios"
import { useEffect } from "react"
import { toast } from "sonner"

type SendOTPResponse = {
	statusCode: number
	statusMessage: string
	otpFlag: boolean
	customerRefId: string
}

type VerifyOTPResponse = {
	status: string
	statusCode: number
	httpStatusCode: number
	isExistingOrderId: boolean
	statusMessage: string
}

type VerifyOTPRequest = {
	mobileNumber: string
	otpCode: string
}

type SendOTPRequest = {
	mobileNumber: string
	isResendOtp: boolean
}

type LoanProvidersResponse = {
	lender_id: string
	batch_id: string
	loan_type: string
	identifier: string
	tenures: {
		batch_lender_course_id: string
		lender_course_id: string
		tenure_in_months: number
		interest_rate: number
		lender_plan_id: string
	}[]
	merchant_id: string
	name: string
	display_name: string
}

type LoanApplyRequest = {
	application_id: string
	lender: string
	lender_id: string
	batch_id: string
	partner_id: string
	currency: string
	applicant_info: {
		first_name: string
		last_name: string
		full_name: string
		email: string
		country_code: string
		phone: string
		dob: string
		relationship: string
		pan_number: string
		pincode: string
		marital_status: string
		employment_type: string
		monthly_income: string
	}
	student_info: {
		first_name: string
		last_name: string
		dob: Date
		full_name: string
	}
	course_info: {
		course_id: string
		batch_lender_course_id: string
	}
	fee_info: {
		fee_grid_revision_id: string
		plan: string
		semester: number
		items: {
			fee_component_id: string
			component_dimension_id: string
			amount: number
			currency: string
			period_type: string
			period_number: number
		}[]
		adjustments: {
			amount: number
			currency: string
			type: string
			applied_by: string
			applied_at: string
			description: string
		}[]
	}
	loan_info: {
		loan_amount: number
		tenure_months: number
		interest_rate: number
		loan_type: string
		merchant_id: string
		lender_course_id: string
		lender_plan_id: string
	}

	additional_details: {
		[key: string]: any
	}

	created_at: string
}

type LoanApplyResponse = {
	loan_id: string
	order_id: string
	redirect_url: string
}

export const useFibe = () => {
	const { applicationId, config } = useFormStore()
	const { loanProviderForm, course_id } = usePayment()
	const queryClient = useQueryClient()

	useEffect(() => {
		if (!config || !window.__DF_MODE) return

		const id = api.interceptors.request.use(req => {
			req.headers["mode"] = window.__DF_MODE
			req.headers["X-Batch-ID"] = config?.batch_id
			req.headers["X-Partner-ID"] = config?.partner_id
			return req
		})

		return () => api.interceptors.request.eject(id)
	}, [config, window.__DF_MODE])

	const useApplyLoan = () => {
		return useMutation<
			LoanApplyResponse,
			AxiosError<{ message: string }>,
			LoanApplyRequest
		>({
			mutationFn: async (data: LoanApplyRequest) => {
				const response = await api.post(
					`universities/${config?.university_id}/batches/${config?.batch_id}/applications/${applicationId}/lenders/${loanProviderForm.form.watch("loan_provider_id")}/apply`,
					data,
				)
				return response.data
			},
			onSuccess: (data: LoanApplyResponse) => {
				toast.success(
					"Loan applied successfully!. Come back when your loan is disbursed",
				)
				// invalidate the payment status query
				queryClient.invalidateQueries({
					queryKey: [
						"check-payment-status",
						applicationId,
						config?.batch_id,
						course_id,
						window.__DF_MODE,
					],
				})
				// Get Loan Details
				queryClient.invalidateQueries({
					queryKey: ["loan-details"],
				})

				// redirect to the redirect_url in another tab
				window.open(data.redirect_url, "_blank")
			},
			onError: (error: AxiosError<{ message: string }>) => {
				logger.info("error while applying loan", error)
				queryClient.invalidateQueries({
					queryKey: [
						"check-payment-status",
						applicationId,
						config?.batch_id,
						course_id,
						window.__DF_MODE,
					],
				})
				toast.error(error.response?.data.message)
			},
		})
	}
	const useSendOTP = () => {
		return useMutation<SendOTPResponse, Error, SendOTPRequest>({
			mutationFn: async (data: SendOTPRequest) => {
				const response = await api.post(
					`/universities/${config?.university_id}/batches/${config?.batch_id}/applications/${applicationId}/lenders/${loanProviderForm.form.watch("loan_provider_id")}/send-otp`,
					data,
				)
				return response.data
			},
			onSuccess: () => {
				toast.success("OTP sent successfully!")
			},
			onError: () => {
				toast.error("Failed to send OTP. Please Try Again!")
			},
		})
	}
	const useVerifyOTP = () => {
		return useMutation<VerifyOTPResponse, Error, VerifyOTPRequest>({
			mutationFn: async (data: VerifyOTPRequest) => {
				const response = await api.post(
					`/universities/${config?.university_id}/batches/${config?.batch_id}/applications/${applicationId}/lenders/${loanProviderForm.form.watch("loan_provider_id")}/verify-otp`,
					data,
				)
				return response.data
			},
			onSuccess: () => {
				toast.success("OTP verified successfully!")
			},
			onError: () => {
				toast.error("Failed to verify OTP. Please Try Again!")
			},
		})
	}

	return {
		useApplyLoan,
		useSendOTP,
		useVerifyOTP,
	}
}

export const useGetLoanProviders = ({
	course_id,
}: {
	course_id: string | null
}) => {
	const { config } = useFormStore()
	const { applicationId } = useFormStore()
	return useQuery<LoanProvidersResponse[]>({
		queryKey: ["loan-providers"],
		enabled: !!course_id,
		queryFn: async () => {
			const response = await api.get(
				`universities/${config?.university_id}/batches/${config?.batch_id}/applications/${applicationId}/lenders?course_id=${course_id}`,
			)
			return response.data
		},
	})
}
